export const PERFORMANCE_METRIC_OPTIONS = [
  {
    id: 'open_rate',
    label: 'Open Rate',
    tableSubtitleMetric: 'Opened',
    sortKey: 'openRate',
  },
  {
    id: 'unsubscribed',
    label: 'Unsubscribed',
    tableSubtitleMetric: 'Unsubscribed',
    sortKey: 'unsubscribed',
  },
  {
    id: 'click_rate',
    label: 'Click Rate',
    tableSubtitleMetric: 'Clicked',
    sortKey: 'clickRate',
  },
  {
    id: 'email_sent',
    label: 'Email sent',
    tableSubtitleMetric: 'Sent',
    sortKey: 'delivered',
  },
  {
    id: 'spam_complaints',
    label: 'Spam Complaints',
    tableSubtitleMetric: 'Spam Complaints',
    sortKey: 'spamComplaints',
  },
];

const EMAIL_STEP_TYPES = new Set(['send_email', 'email', 'send_email_action', 'email_action']);

export function isEmailWorkflowStep(step) {
  if (!step) return false;
  const type = String(step.type || '').toLowerCase();
  return EMAIL_STEP_TYPES.has(type) || type.includes('email');
}

export function extractEmailSteps(steps = []) {
  return (Array.isArray(steps) ? steps : []).filter(isEmailWorkflowStep);
}

export function normalizeEmailStats(step, rawStats = {}) {
  const delivered = Number(rawStats.delivered || 0);
  const opened = Number(rawStats.opened || 0);
  const clicked = Number(rawStats.clicked || 0);
  const unsubscribed = Number(rawStats.unsubscribed || 0);
  const spamComplaints = Number(rawStats.complained || rawStats.spam_complaints || 0);
  const sent = Number(rawStats.total || rawStats.accepted || delivered || 0);

  const openRate = delivered > 0 ? opened / delivered : 0;
  const clickRate = delivered > 0 ? clicked / delivered : 0;
  const unsubscribeRate = delivered > 0 ? unsubscribed / delivered : 0;
  const spamRate = delivered > 0 ? spamComplaints / delivered : 0;

  return {
    id: step.id,
    name: step.label || step.name || step.config?.subject || 'Untitled Email',
    subject: step.config?.subject || step.label || step.name || 'Untitled Email',
    delivered,
    sent,
    opened,
    clicked,
    unsubscribed,
    spamComplaints,
    openRate,
    clickRate,
    unsubscribeRate,
    spamRate,
    hasData: Boolean(rawStats.has_data || delivered > 0 || sent > 0),
  };
}

export function formatPercent(value, digits = 2) {
  if (value == null || Number.isNaN(value)) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}

export function formatCount(value) {
  if (value == null || Number.isNaN(value)) return '—';
  return Number(value).toLocaleString();
}

export function formatOpenRateDisplay(row, showNumbers) {
  if (showNumbers) return formatCount(row.opened);
  return formatPercent(row.openRate);
}

export function formatClickRateDisplay(row, showNumbers) {
  if (showNumbers) return formatCount(row.clicked);
  return formatPercent(row.clickRate);
}

export function formatMetricDisplay(metricId, row, showNumbers) {
  switch (metricId) {
    case 'open_rate':
      return formatOpenRateDisplay(row, showNumbers);
    case 'click_rate':
      return formatClickRateDisplay(row, showNumbers);
    case 'unsubscribed':
      return showNumbers ? formatCount(row.unsubscribed) : formatPercent(row.unsubscribeRate);
    case 'email_sent':
      return formatCount(row.delivered || row.sent);
    case 'spam_complaints':
      return showNumbers ? formatCount(row.spamComplaints) : formatPercent(row.spamRate);
    default:
      return '—';
  }
}

export function getMetricOption(metricId) {
  return PERFORMANCE_METRIC_OPTIONS.find((option) => option.id === metricId)
    || PERFORMANCE_METRIC_OPTIONS[0];
}

export function sortEmailRowsByMetric(rows, metricId) {
  const option = getMetricOption(metricId);
  const key = option.sortKey;
  return [...rows].sort((a, b) => {
    const aVal = Number(a[key] || 0);
    const bVal = Number(b[key] || 0);
    return bVal - aVal;
  });
}

export function aggregateWorkflowEmailStats(rows = []) {
  const totals = rows.reduce(
    (acc, row) => ({
      delivered: acc.delivered + row.delivered,
      opened: acc.opened + row.opened,
      clicked: acc.clicked + row.clicked,
      unsubscribed: acc.unsubscribed + row.unsubscribed,
      spamComplaints: acc.spamComplaints + row.spamComplaints,
      sent: acc.sent + row.sent,
    }),
    { delivered: 0, opened: 0, clicked: 0, unsubscribed: 0, spamComplaints: 0, sent: 0 },
  );

  const delivered = totals.delivered;
  return {
    ...totals,
    openRate: delivered > 0 ? totals.opened / delivered : 0,
    clickRate: delivered > 0 ? totals.clicked / delivered : 0,
    unsubscribeRate: delivered > 0 ? totals.unsubscribed / delivered : 0,
    spamRate: delivered > 0 ? totals.spamComplaints / delivered : 0,
  };
}
